// =============================================================================
// Analytics consent + conversion helpers.
//
// The Google tags (GA4 + optional Google Ads) are loaded in gatsby-ssr.js with
// Google **Consent Mode v2** defaulting every storage type to "denied". Nothing
// that stores a cookie or sends ad/analytics data fires until the visitor
// accepts the cookie banner, at which point Layout calls grantConsent() below.
//
// Microsoft Clarity has no Consent Mode support, so we never inject it until
// explicit acceptance (loadClarity, called from grantConsent).
//
// Every function is SSR-safe (guards typeof window / document).
// =============================================================================

export const CONSENT_COOKIE = "gdpr-okay";

// Legalify Clarity project (created under the Legalify account). Override via
// the Netlify env panel if it ever changes. Loaded only after cookie consent.
const CLARITY_ID = process.env.GATSBY_CLARITY_ID || "wq31ej4fpp";

// Google Ads — only wired once the client creates an Ads account + conversion
// action(s). Until GATSBY_GOOGLE_ADS_ID (+ a label) is set, the Ads branch of
// trackConversion() is a no-op; the GA4 event still fires (audience building).
const ADS_ID = process.env.GATSBY_GOOGLE_ADS_ID || ""; // "AW-XXXXXXXXX"
const ADS_PURCHASE_LABEL = process.env.GATSBY_GOOGLE_ADS_PURCHASE_LABEL || "";
const ADS_LEAD_LABEL = process.env.GATSBY_GOOGLE_ADS_LEAD_LABEL || "";

function gtagSafe() {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return null;
  }
  return window.gtag;
}

// Visitor accepted the banner: flip every Consent Mode signal to granted and
// bring up Clarity.
export function grantConsent() {
  const gtag = gtagSafe();
  if (gtag) {
    gtag("consent", "update", {
      ad_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
      analytics_storage: "granted",
    });
  }
  loadClarity();
}

// Visitor declined: keep everything denied (this is already the SSR default,
// but re-assert in case a previous session had granted).
export function denyConsent() {
  const gtag = gtagSafe();
  if (gtag) {
    gtag("consent", "update", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
    });
  }
}

// Inject Clarity once, only after consent. Idempotent.
export function loadClarity() {
  if (typeof window === "undefined" || !CLARITY_ID || window.__clarityLoaded) {
    return;
  }
  window.__clarityLoaded = true;
  /* eslint-disable */
  (function (c, l, a, r, i, t, y) {
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
    t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", CLARITY_ID);
  /* eslint-enable */
}

// Returning visitors who accepted in a prior session: re-apply consent on load
// so analytics resumes without re-prompting. Called from Layout on mount.
export function initConsentFromCookie() {
  if (typeof document === "undefined") return;
  const entry = document.cookie
    .split("; ")
    .find((c) => c.startsWith(CONSENT_COOKIE + "="));
  if (entry && entry.split("=")[1] === "true") grantConsent();
}

// ---------------------------------------------------------------------------
// Conversion tracking. Call on checkout completion (payment-success page).
//   kind: "purchase" (a paid document/service) | "lead" (devis / meeting)
// Fires a GA4 semantic event always (feeds audiences), and a Google Ads
// conversion only when an Ads id + matching label are configured.
// ---------------------------------------------------------------------------
export function trackConversion({
  kind = "purchase",
  value,
  currency = "CHF",
  transactionId,
  items,
} = {}) {
  const gtag = gtagSafe();
  if (!gtag) return;

  const num = value != null && value !== "" ? Number(value) : undefined;
  const hasValue = num != null && !Number.isNaN(num);

  gtag("event", kind === "lead" ? "generate_lead" : "purchase", {
    ...(hasValue ? { value: num } : {}),
    currency,
    ...(transactionId ? { transaction_id: transactionId } : {}),
    ...(items ? { items } : {}),
  });

  const label = kind === "lead" ? ADS_LEAD_LABEL : ADS_PURCHASE_LABEL;
  if (ADS_ID && label) {
    gtag("event", "conversion", {
      send_to: `${ADS_ID}/${label}`,
      ...(hasValue ? { value: num } : {}),
      currency,
      ...(transactionId ? { transaction_id: transactionId } : {}),
    });
  }
}
